import {
	User,
	Video,
	MapPin,
	CheckCircle2,
	XCircle,
	AlertCircle,
	PlayCircle,
	Check,
} from 'lucide-react';
import { Button } from '../ui';
import { formatTimeRange } from '@/pages/professional/available-dates/utils/formatTimeRange';
import { cn } from '@/lib/utils';
import { SessionCancellationNotice } from '@/components/session/SessionCancellationNotice';
import {
	getSessionCallCompanyBanner,
	isSessionTranscriptionAllowed,
} from '@/constants/sessionCall';
import { useProfessionalSessionAccessWindow } from '@/hooks/useProfessionalSessionAccessWindow';
import { useUserSessionAccessWindow } from '@/hooks/useUserSessionAccessWindow';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { getImageUrl } from '@/utils/imageUrl';
import { formatProfessionalFullName, getSessionDisplayTitle } from '@/utils';
import { openProfessionalSessionCall } from '@/utils/professionalSessionCall';
import { isP2PTranscriptionOnlySessionType } from '@/utils/sessionType';
import { openUserSessionCall } from '@/utils/userSessionCall';
import { toast } from 'react-toastify';
import { E2E_USER_HOME } from '@/constants/e2eSchedulingTestIds';

const MAX_VISIBLE_AVATARS = 4;

type ParticipantDisplay = {
	id: string;
	first_name: string;
	last_name: string;
	profile_photo_url?: string;
};

function participantInitials(first: string, last: string): string {
	const a = first?.trim()[0] ?? '';
	const b = last?.trim()[0] ?? '';
	return (a + b).toUpperCase() || '?';
}

function ParticipantAvatar({
	participant,
	className,
}: {
	participant: ParticipantDisplay;
	className?: string;
}) {
	const [failed, setFailed] = useState(false);
	const src = getImageUrl(participant.profile_photo_url);
	const showImg = src && !failed;

	if (showImg) {
		return (
			<img
				src={src}
				alt={`${participant.first_name} ${participant.last_name}`}
				className={className}
				referrerPolicy="no-referrer"
				onError={() => setFailed(true)}
			/>
		);
	}
	return (
		<span
			className={cn(
				'flex items-center justify-center bg-emerald-100 text-[10px] font-bold text-emerald-700',
				className,
			)}
			aria-hidden
		>
			{participantInitials(participant.first_name, participant.last_name)}
		</span>
	);
}

interface SessionCardProps {
	session: Sessions.Session;
	isProfessionalView: boolean;
	/** ID do usuário logado; na lista de participantes, esse item é destacado (ex.: "Você"). */
	currentUserId?: string;
	/** Quando true, exibe "Pago com benefício" se houver benefício aplicado no pagamento. */
	highlightBenefitPayment?: boolean;
	/** Layout em coluna para containers estreitos (ex.: home com max-width). */
	compact?: boolean;
	handleOnStartSession?: (id: string) => void;
	handleOnEdit?: (session: Sessions.Session) => void;
	handleOnReturn?: (session: Sessions.Session) => void;
	handleOnPayment?: (session: Sessions.Session) => void;
}

const STYLE_MAPS = {
	cardColor: {
		CANCELLED: 'bg-white border-red-200',
		FINISHED: 'bg-zinc-50 border-zinc-200',
		STARTED: 'bg-white border-orange-200',
		CREATED: 'bg-white border-amber-200',
		CONFIRMED: 'bg-white border-zinc-100',
		CLUB_INCLUDED: 'bg-white border-zinc-100',
	},
	tagColor: {
		CANCELLED: 'bg-red-100 text-red-700',
		FINISHED: 'bg-zinc-200 text-zinc-700',
		STARTED: 'bg-orange-100 text-orange-700',
		CREATED: 'bg-amber-100 text-amber-700',
		CONFIRMED: 'bg-emerald-50 text-emerald-700',
		CLUB_INCLUDED: 'bg-emerald-50 text-emerald-700',
	},
	statusText: {
		CANCELLED: 'Cancelada',
		FINISHED: 'Finalizada',
		STARTED: 'Iniciada',
		CREATED: 'Aguardando pagamento',
		CONFIRMED: 'Pago',
		CLUB_INCLUDED: 'Serviço incluso no clube',
	},
	statusIcon: {
		CANCELLED: <XCircle size={14} />,
		FINISHED: <Check size={14} />,
		STARTED: <PlayCircle size={14} />,
		CREATED: <AlertCircle size={14} />,
		CONFIRMED: <CheckCircle2 size={14} />,
		CLUB_INCLUDED: <CheckCircle2 size={14} />,
	},
};

const SessionCard = ({
	session,
	isProfessionalView,
	currentUserId,
	highlightBenefitPayment,
	compact = false,
	handleOnStartSession: _handleOnStartSession,
	handleOnEdit: _handleOnEdit,
	handleOnPayment,
}: SessionCardProps) => {
	const navigate = useNavigate();
	const companyBanner = getSessionCallCompanyBanner(session);
	const [isJoiningCall, setIsJoiningCall] = useState(false);
	const [showOverflow, setShowOverflow] = useState(false);
	const [popoverRect, setPopoverRect] = useState<{
		top: number;
		left: number;
	} | null>(null);
	const triggerRef = useRef<HTMLDivElement>(null);
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const scheduleClose = () => {
		if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
		closeTimeoutRef.current = setTimeout(() => setShowOverflow(false), 120);
	};
	const cancelClose = () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setShowOverflow(true);
	};
	const dateObj = new Date(session.selected_date);
	const formattedTime = formatTimeRange(session.selected_date);
	const isClubIncluded = !!session.included_in_club;
	const isGroupSession = session.type === 'GROUP';
	const statusKey = isClubIncluded
		? 'CLUB_INCLUDED'
		: (session.status as keyof typeof STYLE_MAPS.statusText);

	const paymentsList =
		(session.payments as unknown as Sessions.PaymentDetails[]) || [];
	const hasBenefitApplied =
		!!highlightBenefitPayment &&
		Array.isArray(paymentsList) &&
		paymentsList.some(
			(p) =>
				!!(p as unknown as { payment_benefit_application?: unknown })
					.payment_benefit_application,
		);
	const statusLabel =
		hasBenefitApplied && statusKey === 'CONFIRMED'
			? 'Pago com benefício'
			: STYLE_MAPS.statusText[statusKey];

	// Na visão do usuário, sessão em grupo: até 4 bolinhas + bolinha "+N"; hover mostra todos.
	const guests = session.guests ?? [];
	const apiParticipants = (
		session as {
			participants?: Array<{
				user_id: string;
				user?: {
					id?: string;
					first_name?: string;
					last_name?: string;
					profile_photo_url?: string;
				};
			}>;
		}
	).participants;
	const participantsList: ParticipantDisplay[] =
		isGroupSession && apiParticipants?.length
			? apiParticipants.map((p) => ({
					id: p.user_id,
					first_name: p.user?.first_name ?? '',
					last_name: p.user?.last_name ?? '',
					profile_photo_url: p.user?.profile_photo_url,
			  }))
			: guests;

	const visibleParticipants = participantsList.slice(0, MAX_VISIBLE_AVATARS);
	const overflowParticipants = participantsList.slice(MAX_VISIBLE_AVATARS);
	const hasOverflow = overflowParticipants.length > 0;
	const showParticipantAvatars = isGroupSession
		? participantsList.length > 0
		: guests.length > 0;
	const participantCount =
		participantsList.length ||
		(isGroupSession && typeof session.max_participants === 'number'
			? session.max_participants
			: 0);
	const isUserAccessUnlocked = useUserSessionAccessWindow(session);
	const isProfessionalAccessUnlocked =
		useProfessionalSessionAccessWindow(session);
	const isAccessUnlocked = isProfessionalView
		? isProfessionalAccessUnlocked
		: isUserAccessUnlocked;
	const canAccessSession =
		(session.is_online ||
			(isProfessionalView &&
				isP2PTranscriptionOnlySessionType(session.type))) &&
		(session.status === 'CONFIRMED' || session.status === 'STARTED');

	useLayoutEffect(() => {
		if (
			!showOverflow ||
			!triggerRef.current ||
			participantsList.length === 0
		) {
			setPopoverRect(null);
			return;
		}
		const rect = triggerRef.current.getBoundingClientRect();
		setPopoverRect({
			top: rect.bottom + 2,
			left: rect.left,
		});
	}, [showOverflow, participantsList.length]);

	useEffect(
		() => () => {
			if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
		},
		[],
	);

	const serviceName = getSessionDisplayTitle(session);

	const professionalName = session.professional
		? formatProfessionalFullName(
				session.professional as Pick<
					Professionals.Professional,
					'name_prefix' | 'first_name' | 'last_name'
				>,
		  )
		: '';

	return (
		<div
			data-testid={`${E2E_USER_HOME.SESSION_CARD_PREFIX}${session.id}`}
			className={cn(
				'flex flex-col items-stretch gap-3 rounded-3xl border p-4 shadow-sm transition-all',
				!compact && 'md:flex-row md:items-center',
				STYLE_MAPS.cardColor[statusKey],
			)}
		>
			<div
				className={cn(
					'flex w-full flex-row items-center gap-4',
					!compact && 'md:contents',
				)}
			>
				<div
					className={cn(
						'flex h-[64px] shrink-0 items-center overflow-hidden rounded-2xl border border-zinc-200 bg-white',
						compact && 'h-[56px]',
					)}
				>
					<div className={cn('px-5 text-center', compact && 'px-3')}>
						<span className="text-xl font-black text-zinc-800">
							{String(dateObj.getDate()).padStart(2, '0')}
						</span>
						<p className="text-[10px] font-bold uppercase text-zinc-400">
							{dateObj
								.toLocaleString('pt-BR', { month: 'short' })
								.replace('.', '')}
						</p>
					</div>
					<div className="h-10 w-[1px] bg-zinc-200" />
					<div className={cn('px-5 text-center', compact && 'px-3')}>
						<span
							className={cn(
								'text-xl font-black text-zinc-800',
								compact && 'text-lg',
							)}
						>
							{formattedTime.timeRange.split(' ')[0]}
						</span>
						<p className="text-[10px] font-bold uppercase text-zinc-400">
							HORÁRIO
						</p>
					</div>
				</div>

				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<div
						className={cn(
							'flex flex-col gap-2',
							!compact &&
								'md:grid md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-x-3 md:gap-y-1',
						)}
					>
						<h3 className="text-sm font-bold text-zinc-800 md:text-base">
							{serviceName}
						</h3>
						<div
							className={cn(
								'inline-flex w-fit max-w-full items-center gap-1 rounded-full border border-current px-3 py-1 text-[10px] font-bold',
								!compact &&
									'md:row-span-2 md:self-center md:justify-self-end',
								STYLE_MAPS.tagColor[statusKey],
							)}
						>
							{STYLE_MAPS.statusIcon[statusKey]}
							<span className="truncate text-[10px] leading-tight md:text-xs">
								{statusLabel}
							</span>
						</div>

						<SessionCancellationNotice
							session={session}
							viewer={
								isProfessionalView ? 'professional' : 'user'
							}
							compact
							className="md:col-span-2"
						/>

						<div className="flex flex-col gap-1.5">
							<div className="flex min-w-0 items-center gap-2">
								<div className="shrink-0 rounded-full border bg-zinc-50 p-1">
									<User size={12} className="text-zinc-400" />
								</div>
								<span className="truncate text-xs font-medium text-zinc-400 md:text-sm">
									{isProfessionalView &&
									isGroupSession &&
									participantCount > 0
										? `Sessão em grupo · ${participantCount} participante${
												participantCount === 1
													? ''
													: 's'
										  }`
										: isProfessionalView && session.user
										  ? `${session.user.first_name} ${session.user.last_name}`
										  : professionalName}
								</span>
							</div>
							{showParticipantAvatars && (
								<div
									ref={triggerRef}
									className="relative flex w-fit items-center -space-x-2"
									onMouseEnter={cancelClose}
									onMouseLeave={scheduleClose}
								>
									{visibleParticipants.map((p) => (
										<div
											key={p.id}
											className="relative h-7 w-7 overflow-hidden rounded-full border border-zinc-200 bg-zinc-100"
										>
											<ParticipantAvatar
												participant={p}
												className="h-full w-full rounded-full object-cover"
											/>
										</div>
									))}
									{hasOverflow && (
										<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-zinc-100 text-xs font-bold text-emerald-600">
											+{overflowParticipants.length}
										</div>
									)}
									{showOverflow &&
										popoverRect &&
										participantsList.length > 0 &&
										createPortal(
											<div
												className="fixed z-[9999] max-h-[280px] w-max max-w-[280px] overflow-y-auto overflow-x-hidden rounded-lg border border-zinc-200 bg-white p-2 shadow-lg"
												style={{
													top: popoverRect.top,
													left: popoverRect.left,
												}}
												onMouseEnter={cancelClose}
												onMouseLeave={scheduleClose}
											>
												{participantsList.map((p) => {
													const isCurrentUser =
														!!currentUserId &&
														p.id === currentUserId;
													return (
														<div
															key={p.id}
															className={cn(
																'flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1.5',
																isCurrentUser &&
																	'bg-emerald-50 ring-1 ring-emerald-200/80',
															)}
														>
															<div className="h-7 w-7 shrink-0 overflow-hidden rounded-full border border-zinc-200">
																<ParticipantAvatar
																	participant={
																		p
																	}
																	className="h-full w-full object-cover"
																/>
															</div>
															<span className="min-w-0 truncate text-sm text-zinc-700">
																{p.first_name}{' '}
																{p.last_name}
																{isCurrentUser && (
																	<span className="ml-1 font-medium text-emerald-700">
																		(Você)
																	</span>
																)}
															</span>
														</div>
													);
												})}
											</div>,
											document.body,
										)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<div
				className={cn(
					'flex w-full flex-wrap items-center gap-2',
					compact && 'border-t border-zinc-100 pt-3',
					!compact && 'md:w-auto md:flex-nowrap',
				)}
			>
				<div className="flex shrink-0 items-center gap-2 text-[10px] font-bold uppercase text-zinc-500">
					{session.is_online ? (
						<Video size={16} />
					) : (
						<MapPin size={16} />
					)}
					{session.is_online ? 'Online' : 'Presencial'}
				</div>
				<div className="ml-auto flex shrink-0 flex-wrap justify-end gap-2">
					{!isProfessionalView && session.status === 'CREATED' && (
						<Button
							text="Pagar"
							data-testid={`${E2E_USER_HOME.SESSION_PAY_PREFIX}${session.id}`}
							onClick={() => handleOnPayment?.(session)}
							className="rounded-xl bg-emerald-600 text-white"
						/>
					)}
					{canAccessSession && (
						<Button
							type="button"
							text={
								isJoiningCall
									? isProfessionalView
										? 'Entrando...'
										: 'Acessando...'
									: isProfessionalView
									  ? isAccessUnlocked
											? isP2PTranscriptionOnlySessionType(
													session.type,
											  )
												? 'Iniciar transcrição'
												: 'Entrar na sessão'
											: 'Disponível 5 min antes'
									  : isAccessUnlocked
									    ? 'Acessar'
									    : 'Disponível 5 min antes'
							}
							onClick={async () => {
								if (!isAccessUnlocked) {
									toast.info(
										isProfessionalView
											? 'Só é possível entrar nos últimos 5 minutos antes do horário agendado.'
											: 'O acesso será liberado 5 minutos antes da sessão.',
									);
									return;
								}
								if (isProfessionalView) {
									await openProfessionalSessionCall({
										sessionId: session.id,
										navigate,
										sessionStatus: session.status,
										sessionType: session.type,
										isOnline: session.is_online,
										transcriptionAllowed:
											isSessionTranscriptionAllowed(
												session,
											),
										transcriptionMock:
											session.transcription_mock === true,
										companyBanner,
										onStart: () => setIsJoiningCall(true),
										onFinally: () =>
											setIsJoiningCall(false),
									});
									return;
								}
								await openUserSessionCall({
									sessionId: session.id,
									navigate,
									sessionStatus: session.status,
									onStart: () => setIsJoiningCall(true),
									onFinally: () => setIsJoiningCall(false),
								});
							}}
							disabled={isJoiningCall || !isAccessUnlocked}
							className={cn(
								'rounded-xl text-white',
								isJoiningCall || !isAccessUnlocked
									? 'cursor-not-allowed bg-zinc-300'
									: 'bg-zinc-900 hover:bg-zinc-800',
							)}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

const SessionCardSkeleton = () => (
	<div className="flex animate-pulse gap-6 rounded-2xl border bg-zinc-50 p-6">
		<div className="h-16 w-16 rounded-xl bg-zinc-200" />
		<div className="flex-1 space-y-3">
			<div className="h-5 w-48 rounded bg-zinc-200" />
			<div className="h-4 w-32 rounded bg-zinc-200" />
		</div>
	</div>
);

export { SessionCard, SessionCardSkeleton };
